/**
 * The card primitive: a hairline-bordered surface with an optional header block.
 * shadcn/ui's composition (Card / CardHeader / CardTitle / CardDescription /
 * CardContent / CardFooter), minus the pieces this app has no use for.
 */
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'rounded-card border border-line bg-surface shadow-card',
        className,
      )}
      {...props}
    />
  );
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex items-start justify-between gap-3 px-5 pt-4 pb-3', className)} {...props} />;
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn('text-[0.9375rem] font-semibold tracking-[-0.01em]', className)} {...props} />;
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('mt-0.5 text-xs text-muted', className)} {...props} />;
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 pb-5', className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-wrap items-center gap-2 border-t border-line-soft px-5 py-3', className)}
      {...props}
    />
  );
}

/**
 * A labelled figure -- the "stat tile" of the card grid. `label` above, `value`
 * large, an optional `hint` under it. Takes nodes rather than strings so a call
 * site can drop a <StatusPill> into the value slot without this file learning
 * what a pill is.
 */
export function CardStat({ label, value, hint, className }: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="text-[0.6875rem] font-semibold uppercase tracking-[0.055em] text-muted">{label}</div>
      <div className="mt-1 truncate text-[0.9375rem] font-medium text-text">{value}</div>
      {hint === undefined ? null : <div className="mt-0.5 text-xs text-muted">{hint}</div>}
    </div>
  );
}
